import type { RouterType } from "@/lib/router";
import type { ExtendedActiveOptions, TabWithIndicatorProps } from "./types";

type Location = {
  pathname: string;
  search: string;
  hash: string;
};

/**
 * タブのアクティブ状態を判定する関数
 *
 * TanStack Routerのアクティブ状態判定ロジックを実装
 * exact, includeSearch, includeHashオプションに対応
 *
 * @param to ターゲットURLパス
 * @param location 現在のロケーション情報
 * @param activeOptions TanStack Routerのアクティブオプション
 * @param search ターゲットのクエリパラメータ
 * @param currentSearch 現在のパースされたクエリパラメータ（TanStack Routerから取得）
 * @returns アクティブかどうか
 */
export function isTabActive(
  to: string,
  location: Location,
  activeOptions?: ExtendedActiveOptions,
  search?: Record<string, unknown>,
  currentSearch?: Record<string, unknown>,
): boolean {
  // 無効なパスの場合は非アクティブ
  if (!to) return false;

  let isActive = false;

  // 完全一致（exact: true）の場合
  if (activeOptions?.exact) {
    isActive = matchPathWithPlaceholder(location.pathname, to);
  }
  // プレフィックスマッチ（デフォルト動作）- ルート以外の場合
  else {
    isActive = to !== "/" && location.pathname.startsWith(to);
  }

  // クエリパラメータの考慮
  if (isActive && activeOptions?.includeSearch && search && currentSearch) {
    // TanStack Routerでパースされたオブジェクト同士を直接比較
    for (const [key, targetValue] of Object.entries(search)) {
      if (targetValue !== undefined && targetValue !== null) {
        const currentValue = currentSearch[key];
        const targetStr = String(targetValue);
        const currentStr = currentValue ? String(currentValue) : undefined;

        if (currentStr !== targetStr) {
          // 末尾の~文字による不一致の場合は、~を除いて再比較
          const normalizedCurrent = currentStr?.endsWith("~")
            ? currentStr.slice(0, -1)
            : currentStr;
          const normalizedTarget = targetStr.endsWith("~")
            ? targetStr.slice(0, -1)
            : targetStr;
          if (normalizedCurrent !== normalizedTarget) {
            isActive = false;
            break;
          }
        }
      }
    }
  } else if (isActive && activeOptions?.includeSearch === false) {
    // location.searchではなくwindow.location.searchに依存しない実装
    isActive = !location.search || location.search === "";
  }

  // ハッシュフラグメントの考慮
  if (isActive && activeOptions?.includeHash && location.hash) {
    const currentHash = location.hash.startsWith("#")
      ? location.hash.slice(1) // # を除去
      : location.hash;

    // 拡張された型を使用してハッシュを安全に取得
    const targetHash = activeOptions.hash || "";

    // ハッシュが指定されていて、現在のハッシュと一致する場合のみアクティブ
    isActive = targetHash !== "" && currentHash === targetHash;
  }

  return isActive;
}

function matchPathWithPlaceholder(path: string, pattern: string): boolean {
  // $で始まるパラメータを正規表現のワイルドカードに置換
  const regexPattern = pattern.replace(/\$[a-zA-Z0-9_]+/g, "[^/]+");
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(path);
}

/**
 * タブ配列にアクティブ状態を適用する関数
 *
 * 各タブに対してアクティブ状態を判定し、新しい配列を返す（不変性を維持）
 *
 * @param tabs タブの配列
 * @param location 現在のロケーション情報
 * @param router パス解決に使用するTanStack Routerインスタンス
 * @param currentSearch 現在のパースされたクエリパラメータ（TanStack Routerから取得）
 * @returns アクティブ状態が設定された新しいタブ配列
 */
export function applyActiveState(
  tabs: TabWithIndicatorProps[],
  location: Location,
  router?: RouterType,
  currentSearch?: Record<string, unknown>,
): TabWithIndicatorProps[] {
  return tabs.map((tab) => {
    let resolvedPath: string = typeof tab.to === "string" ? tab.to : "";

    if (router && tab.params) {
      const builtLocation = router.buildLocation({
        to: tab.to,
        params: tab.params,
      });
      resolvedPath = builtLocation.pathname;
    }

    return {
      ...tab,
      active: isTabActive(
        resolvedPath,
        location,
        tab.activeOptions,
        typeof tab.search === "object" &&
          tab.search !== null &&
          !Array.isArray(tab.search)
          ? (tab.search as Record<string, unknown>)
          : undefined,
        currentSearch,
      ),
    };
  });
}
